import * as React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslationProgress } from "@/hooks/useTranslationProgress";

// Only English and Spanish supported
const LANGUAGES = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇻🇪" },
] as const;

export default function LanguageSelector({
  compact = false,
  silent = false,
}: {
  compact?: boolean;
  silent?: boolean;
}) {
  const { language, setLanguage, isTranslating } = useLanguage();
  const translationProgress = useTranslationProgress(language);

  const isProcessing = isTranslating || translationProgress.status === "running";
  const progressText =
    translationProgress.status === "running" &&
    translationProgress.progress &&
    translationProgress.total
      ? `${Math.min(
          100,
          Math.round((translationProgress.progress / translationProgress.total) * 100),
        )}%`
      : null;

  const normalizedLanguage =
    LANGUAGES.find((l) => l.code === language)?.code || "en";
  const current = LANGUAGES.find((l) => l.code === normalizedLanguage)!;
  const next = LANGUAGES.find((l) => l.code !== normalizedLanguage)!;

  // Language changes apply silently (no notification of any kind).
  const toggleLanguage = async () => {
    await setLanguage(next.code as any);
  };


  return (
    <div className="flex items-center gap-2 relative z-[9999] pointer-events-auto">
      <button
        type="button"
        onClick={toggleLanguage}
        disabled={isProcessing}
        aria-label={`Switch language to ${next.name}`}
        title={next.name}
        className={cn(
          "inline-flex items-center justify-center rounded-full leading-none cursor-pointer touch-manipulation transition-transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-60",
          compact ? "h-9 w-9 text-xl" : "h-11 w-11 text-2xl",
        )}
      >
        <span aria-hidden="true">{current.flag}</span>
      </button>
      {isProcessing && (
        <div className="flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin text-primary" />
          {progressText && (
            <span className="text-xs text-muted-foreground">{progressText}</span>
          )}
        </div>
      )}
    </div>
  );
}
